import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, BatteryMedium, Flame, ShieldCheck } from "lucide-react";
import { offerMessage, waLink, warrantyOf } from "@/lib/store";
import { PriceBlock } from "./PriceBlock";
import { useInterestClick } from "@/lib/cart";
import { PhotoGallery } from "./PhotoGallery";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Reveal, EASE } from "./Reveal";
import { Countdown } from "./Countdown";
import { ConditionChip } from "./Condition";
import { AppleMark, isAllOriginal } from "./PartsInfo";
import { AddToCartButton } from "./AddToCartButton";


export const FeaturedOffer = ({ product: p }) => {
  const [expired, setExpired] = useState(false);
  const onExpire = useCallback(() => setExpired(true), []);
  const interest = useInterestClick(p || {}, { offer: true });
  const ended = p?.featured_until && new Date(p.featured_until).getTime() <= Date.now();
  if (!p || expired || ended) return null;
  const specs = [p.storage, p.color].filter(Boolean);
  return (
    <section data-testid="featured-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 pb-20 sm:pb-28">
      <div className="grid grid-cols-1 overflow-hidden rounded-[32px] border border-white/15 bg-[#111113] lg:grid-cols-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="group relative aspect-[4/5] sm:aspect-square lg:col-span-5 lg:aspect-[4/5] bg-zinc-950"
        >
          <motion.div
            variants={{ hidden: { clipPath: "inset(0% 100% 0% 0%)" }, show: { clipPath: "inset(0% 0% 0% 0%)" } }}
            transition={{ duration: 1.4, ease: EASE }}
            className="absolute inset-0"
          >
            <PhotoGallery images={p.images} alt={p.model} id={`featured-${p.id}`} to={`/produto/${p.id}`} />
          </motion.div>
        </motion.div>
        <Reveal className="relative flex flex-col justify-between gap-10 p-7 sm:p-10 lg:col-span-7 lg:p-12">
          <div>
            <span data-testid="featured-badge" className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-black">
              <Flame className="h-4 w-4" /> Oferta da semana
            </span>
            <Link to={`/produto/${p.id}`} data-testid="featured-link" className="block hover:opacity-90">
              <h2 data-testid="featured-model" className="font-display mt-8 break-words text-5xl sm:text-6xl font-extrabold leading-[0.95] tracking-[-0.04em] text-white">{p.model}</h2>
            </Link>
            <div className="mt-6 flex flex-wrap gap-2">
              {specs.map((s) => <span key={s} className="rounded-full border border-white/15 px-3 py-1 text-sm text-zinc-200">{s}</span>)}
              <ConditionChip p={p} testid="featured-condition" />
              {p.battery != null && (
                <span className="flex items-center gap-1.5 rounded-full border border-emerald-300/30 px-3 py-1 text-sm text-emerald-300"><BatteryMedium className="h-4 w-4" /> Bateria {p.battery}%</span>
              )}
              {isAllOriginal(p) && (
                <span data-testid="featured-original-seal" title="Todas as peças originais de fábrica" className="flex items-center gap-1.5 rounded-full border border-white/25 bg-white/[0.06] px-3 py-1 text-sm font-medium text-white"><AppleMark className="h-3.5 w-3.5" /> Original Apple</span>
              )}
            </div>
            {p.notes && <p className="mt-6 max-w-md text-zinc-300 leading-relaxed">{p.notes}</p>}
            <p data-testid="featured-warranty" className="mt-6 flex items-center gap-2 font-medium text-emerald-300"><ShieldCheck className="h-5 w-5 shrink-0" /> {warrantyOf(p)}</p>
            {p.featured_until && <div className="mt-8"><Countdown until={p.featured_until} onExpire={onExpire} /></div>}
          </div>
          <div>
            <p className="mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">Valor</p>
            <div className="mt-2"><PriceBlock p={p} size="featured" priceTestid="featured-price" installmentTestid="featured-installment" /></div>
            <a data-testid="featured-whatsapp-btn" data-pid={p.id} data-pmodel={p.model} href={waLink(offerMessage(p))} onClick={interest} target="_blank" rel="noopener noreferrer"
              className="group/btn mt-8 flex w-full items-center justify-between gap-4 rounded-full bg-[#25D366] py-3 pl-7 pr-3 text-black transition-transform duration-300 hover:scale-[1.02] active:scale-95">
              <span className="flex items-center gap-3 text-base font-bold"><WhatsAppIcon className="h-5 w-5" /> Quero esse</span>
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black text-white transition-transform duration-500 group-hover/btn:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
            </a>
            <div className="mt-3"><AddToCartButton p={p} testid="featured-add-to-cart" /></div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
